import { Form, notification } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import useTaskManager from '../../context/taskManager/useTaskManager';
import {
  Capability,
  File as ApiFile,
  FollowUpPrompt,
  Task,
  TaskDefinition,
  TaskMessage,
  TaskMessageInput,
  TemplateFieldRole,
} from '../../graphql/schema';
import { isImageFile } from '../../helper/files';
import uploadFormFiles from '../../helper/uploadFormFiles';
import useCreateGenericMessage from '../../stream/useCreateGenericMessage';
import useCreateMessage from '../../stream/useCreateMessage';
import useCreateMessageWithFollowUpPrompt from '../../stream/useCreateMessageWithFollowUpPrompt';
import EditMessage from './EditMessage';
import FollowUpPrompts from './FollowUpPrompts';
import Message, {
  TaskMessageLoadingState,
  TaskMessageWithLoadingIndicator,
} from './Message';
import TaskDefinitionHeader from './TaskDefinitionHeader';
import TaskPrompt, { TaskPromptFormFields } from './TaskPrompt';

interface Props {
  task: Task;
  capability?: Capability;
  taskDefinition?: TaskDefinition;
  reloadTask: () => void;
}

const TaskView = ({ task, taskDefinition, reloadTask, capability }: Props) => {
  const [notificationApi, notificationContextHolder] =
    notification.useNotification();

  const { claimNewTaskMessage } = useTaskManager();

  const [newMessagePlaceholder, setNewMessagePlaceholder] =
    useState<TaskMessageWithLoadingIndicator>();

  const [promptForm] = Form.useForm<TaskPromptFormFields>();

  const [selectedMessage, setSelectedMessage] = useState<TaskMessage>();

  const createMessage = useCreateMessage(task.id);
  const createMessageUsingFollowUpPrompt = useCreateMessageWithFollowUpPrompt(
    task.id,
  );
  const createMessageUsingGenericPrompt = useCreateGenericMessage(task.id);

  const loadedTaskId = useRef<string | undefined>();
  const numberOfLoadedMessages = useRef<number | undefined>();

  const scrollToBottom = useCallback(() => {
    window.document.getElementById('messagesContainer')?.scrollTo({
      top: 999999999,
    });
  }, []);

  useEffect(() => {
    if (newMessagePlaceholder) {
      promptForm.setFieldsValue({
        knowledgeBaseIds:
          newMessagePlaceholder.input?.prompt?.knowledgeBaseIds ?? null,
      });
      return;
    }

    const knowledgeBaseIds = task.messages?.slice(-1)?.pop()?.input
      .prompt?.knowledgeBaseIds;
    promptForm.setFieldsValue({
      knowledgeBaseIds,
    });
  }, [task.id, task.messages, newMessagePlaceholder]);

  useEffect(() => {
    // Scroll down when messages change
    scrollToBottom();
  }, [task.messages, newMessagePlaceholder, scrollToBottom]);

  useEffect(() => {
    loadedTaskId.current = task.id;

    if (
      task.id !== loadedTaskId.current ||
      numberOfLoadedMessages.current !== task.messages?.length
    ) {
      // Task changed or new messages are available
      numberOfLoadedMessages.current = task.messages?.length;

      // Reset on task change
      setSelectedMessage(undefined);

      // Reset placeholder
      setNewMessagePlaceholder(undefined);
    }

    const newTaskMessage = claimNewTaskMessage();
    if (newTaskMessage) {
      // Handle new message requests passed from other pages
      if (newTaskMessage.taskId === task.id) {
        // Add message
        if (newTaskMessage.prompt)
          void handleAddMessageUsingGenericPrompt(newTaskMessage.prompt);
        if (newTaskMessage.formValues)
          void handleAddMessageUsingTaskDefinitionForm(
            newTaskMessage.formValues,
          );
        if (newTaskMessage.followUpPrompt)
          void handleAddMessageUsingFollowUpPrompt(
            newTaskMessage.followUpPrompt,
          );
      }
    }
  }, [task, claimNewTaskMessage]);

  const addTextChunk = useCallback((textChunk: string) => {
    setNewMessagePlaceholder((message) =>
      message
        ? {
            ...message,
            response: {
              text: `${message.response?.text ?? ''}${textChunk}`,
            },
          }
        : message,
    );
  }, []);

  const reportToolCall = useCallback((name: string) => {
    if (name === 'generate_image') {
      setNewMessagePlaceholder((msg) =>
        msg
          ? {
              ...msg,
              loading: TaskMessageLoadingState.GENERATING_IMAGE,
            }
          : undefined,
      );
      return;
    }

    if (name === 'web_search') {
      setNewMessagePlaceholder((msg) =>
        msg
          ? {
              ...msg,
              loading: TaskMessageLoadingState.SEARCHING_WEB,
            }
          : undefined,
      );
    }
  }, []);

  const addMessage = useCallback(
    async (input: TaskMessageInput) => {
      // Add placeholder message
      setNewMessagePlaceholder({
        id: 'NEW',
        createdAt: new Date(),
        input,
        response: {},
        loading: TaskMessageLoadingState.INITIALIZING,
      });

      try {
        // Create message

        if (input.formValues) {
          await createMessage(
            await uploadFormFiles(
              'task',
              input.formValues,
              task.fileUploadInfo,
            ),
            addTextChunk,
            reportToolCall,
          );
        } else if (input.prompt) {
          await createMessageUsingGenericPrompt(
            await uploadFormFiles('task', input.prompt, task.fileUploadInfo),
            addTextChunk,
            reportToolCall,
          );
        } else if (input.followUpPrompt) {
          await createMessageUsingFollowUpPrompt(
            input.followUpPrompt,
            addTextChunk,
            reportToolCall,
          );
        }

        reloadTask();
      } catch (error: any) {
        console.error(error);
        notificationApi.error({
          message: 'Ups...',
          description:
            '...da hat etwas nicht funktioniert. Bitte versuche es erneut.',
          closable: true,
          duration: null,
        });

        setNewMessagePlaceholder(undefined);
      }
    },
    [
      addTextChunk,
      createMessageUsingFollowUpPrompt,
      task,
      reloadTask,
      createMessage,
      createMessageUsingGenericPrompt,
      reportToolCall,
      notificationApi,
    ],
  );

  const getMessageInputKeys = useCallback(() => {
    const imagesKey =
      capability?.messageTemplateFields.find(
        (field) => field.role === TemplateFieldRole.IMAGES,
      )?.name ?? 'images';
    const filesKey =
      capability?.messageTemplateFields.find(
        (field) => field.role === TemplateFieldRole.FILES,
      )?.name ?? 'files';
    const promptKey =
      capability?.messageTemplateFields.find(
        (field) => field.role === TemplateFieldRole.PROMPT,
      )?.name ?? 'prompt';
    const knowledgeBaseIdsKey =
      capability?.messageTemplateFields.find(
        (field) => field.role === TemplateFieldRole.KNOWLEDGE_BASE_IDS,
      )?.name ?? 'knowledgeBaseIds';

    return {
      imagesKey,
      filesKey,
      promptKey,
      knowledgeBaseIdsKey,
    };
  }, [capability]);

  const handleEditMessage = useCallback(
    (message: TaskMessage) => () => {
      const { formValues, followUpPrompt, prompt } = message.input;

      if (formValues) {
        setSelectedMessage(message);
      } else if (followUpPrompt) {
        // Not possible
      } else if (prompt) {
        // Find fields for images & files and move all items to files form field
        const files: ApiFile[] = [];

        const { imagesKey, filesKey, promptKey, knowledgeBaseIdsKey } =
          getMessageInputKeys();

        if (imagesKey && prompt[imagesKey]) {
          files.push(...prompt[imagesKey]);
        }
        if (filesKey && prompt[filesKey]) {
          files.push(...prompt[filesKey]);
        }

        promptForm.setFieldsValue({
          prompt: message.input.prompt[promptKey] ?? '',
          knowledgeBaseIds: message.input.prompt[knowledgeBaseIdsKey],
          files,
        });
        document.getElementById('promptTextField')?.focus();
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    },
    [promptForm, getMessageInputKeys, scrollToBottom],
  );

  const handleAddMessageUsingGenericPrompt = useCallback(
    async (prompt: TaskPromptFormFields) => {
      const message: any = {};

      // Find message fields for images & files
      const { imagesKey, filesKey, promptKey, knowledgeBaseIdsKey } =
        getMessageInputKeys();

      const images = prompt.files?.filter((file) => isImageFile(file));
      const files = prompt.files?.filter((file) => !isImageFile(file));

      if (images?.length) message[imagesKey] = images;
      if (files?.length) message[filesKey] = files;

      message[knowledgeBaseIdsKey] = prompt.knowledgeBaseIds;

      message[promptKey] = prompt.prompt;

      await addMessage({
        prompt: message,
      });
    },
    [addMessage, getMessageInputKeys],
  );

  const handleAddMessageUsingTaskDefinitionForm = useCallback(
    async (formValues: any) => {
      setSelectedMessage(undefined);
      addMessage({
        formValues,
      });
    },
    [addMessage],
  );

  const handleAddMessageUsingFollowUpPrompt = useCallback(
    async (followUpPrompt: FollowUpPrompt) => {
      addMessage({
        followUpPrompt,
      });
    },
    [addMessage],
  );

  return (
    <>
      {notificationContextHolder}

      {selectedMessage && taskDefinition && (
        <EditMessage
          message={selectedMessage}
          taskDefinition={taskDefinition}
          onClose={() => setSelectedMessage(undefined)}
          onSave={handleAddMessageUsingTaskDefinitionForm}
        />
      )}

      {!selectedMessage && (
        <>
          <div
            style={{
              overflowY: 'auto',
              flex: '1 1 auto',
              height: 0,
            }}
            className="flex items-center flex-col"
            id="messagesContainer"
          >
            {taskDefinition && (
              <div className="w-full p-5">
                <TaskDefinitionHeader taskDefinition={taskDefinition} />
              </div>
            )}
            <div className="flex flex-col space-y-6 py-5 w-full md:max-w-3xl lg:max-w-[40rem] xl:max-w-[48rem]">
              {task.messages?.map((message) => (
                <Message
                  message={message}
                  onEditMessage={handleEditMessage(message)}
                  formFieldDefinitions={taskDefinition?.formDefinition ?? []}
                  key={message.id}
                />
              ))}
              {newMessagePlaceholder && (
                <Message
                  message={newMessagePlaceholder}
                  formFieldDefinitions={taskDefinition?.formDefinition ?? []}
                  key="NEW"
                />
              )}
            </div>

            <FollowUpPrompts
              task={task}
              onSelect={handleAddMessageUsingFollowUpPrompt}
            />
          </div>
          <TaskPrompt
            form={promptForm}
            onSend={handleAddMessageUsingGenericPrompt}
            capability={capability}
          />
        </>
      )}
    </>
  );
};

export default TaskView;
